<!--
 * Add-to-home-screen prompt / 「保存到桌面」引导弹窗
 *
 * Android Chrome: one-tap install button backed by the captured
 * `beforeinstallprompt` event (real WebAPK install).
 * iOS Safari: step-by-step guide — Apple provides no trigger API, the guide
 * is the platform ceiling, not a design choice.
 *
 * Shows at most ONCE per venue (promptRecord), only on pickup URLs (no table
 * number), only when the venue's backend flag enables it, and never inside
 * in-app webviews (platform.js suppresses those — saving is impossible there).
-->
<style scoped lang="sass">
.a2hs_content
  display: flex
  flex-direction: column
  align-items: center
  padding-top: 30PX

  .a2hs_logo
    width: 64PX
    height: 64PX
    border-radius: 14PX
    object-fit: cover
    margin-bottom: 12PX

  .a2hs_title
    color: var(--menu-primary-text-colour)
    font-size: 18PX
    font-weight: bold
    text-align: center
    margin-bottom: 8PX

  .a2hs_benefit
    font-size: 14PX
    text-align: center
    opacity: .75
    margin-bottom: 18PX

  .a2hs_btn
    border-radius: 5PX

  .a2hs_later
    padding: 12PX
    font-size: 14PX
    opacity: .6
    cursor: pointer

// 全屏引导遮罩（不在 .a2hs_content 里，fixed 定位覆盖整个视口）。
// 实底卡片承载所有文字（业内通行模式），遮罩本身不用太黑，加毛玻璃即可
.a2hs_overlay
  position: fixed
  top: 0
  left: 0
  right: 0
  bottom: 0
  z-index: 3000
  background: rgba(0, 0, 0, .55)
  -webkit-backdrop-filter: blur(8px)
  backdrop-filter: blur(8px)
  animation: a2hs-overlay-in .25s ease-out

  // 步骤卡片：白底圆角，默认锚在底部（分享按钮上方），入口在顶部的形态
  // （iPad / Android）改锚顶部
  .a2hs_guide_card
    position: fixed
    left: 16PX
    right: 16PX
    bottom: 84PX
    padding: 20PX 20PX 12PX
    border-radius: 16PX
    background: #fff
    color: #1c1c1e
    box-shadow: 0 12PX 40PX rgba(0, 0, 0, .4)
    animation: a2hs-card-in .3s ease-out

    &.a2hs_guide_card_top
      bottom: auto
      top: 78PX

  .a2hs_guide_title
    font-size: 17PX
    font-weight: bold
    line-height: 24PX
    margin-bottom: 14PX

  .a2hs_guide_step
    display: flex
    align-items: center
    padding: 7PX 0
    font-size: 15PX
    line-height: 21PX
    text-align: left

  .a2hs_guide_step_icon
    flex-shrink: 0
    display: flex
    align-items: center
    justify-content: center
    width: 38PX
    height: 38PX
    margin-right: 12PX
    border-radius: 10PX
    background: rgba(0, 0, 0, .05)
    color: var(--menu-primary-background-colour)

  .a2hs_guide_close
    margin-top: 10PX
    text-align: center
    font-size: 12PX
    color: rgba(28, 28, 30, .45)

  // 弹跳箭头：iPhone 指向底部中央的分享按钮
  .a2hs_arrow
    position: fixed
    bottom: 16PX
    left: 50%
    margin-left: -24PX
    filter: drop-shadow(0 2PX 6PX rgba(0, 0, 0, .5))
    animation: a2hs-bounce 1.1s ease-in-out infinite

    // iPad 的分享按钮和 Android Chrome 的 ⋮ 菜单都在右上角：移到右上、翻转指向上方
    &.a2hs_arrow_ipad
      bottom: auto
      top: 14PX
      left: auto
      right: 18PX
      margin-left: 0
      transform: rotate(180deg)
      animation-name: a2hs-bounce-up

    // 三星浏览器的 ≡ 菜单在右下角：移到右下，仍指向下方
    &.a2hs_arrow_bottom_right
      left: auto
      right: 22PX
      margin-left: 0

@keyframes a2hs-overlay-in
  from
    opacity: 0
  to
    opacity: 1

@keyframes a2hs-card-in
  from
    opacity: 0
    transform: translateY(14PX)
  to
    opacity: 1
    transform: translateY(0)

@keyframes a2hs-bounce
  0%, 100%
    transform: translateY(0)
  50%
    transform: translateY(-12PX)

@keyframes a2hs-bounce-up
  0%, 100%
    transform: rotate(180deg) translateY(0)
  50%
    transform: rotate(180deg) translateY(-12PX)
</style>

<template lang="pug">
//- 刻意不用 v-model：v-model + @changeShow 双绑定时 Vue 2 会把 v-model 回调排前，
//- onDialogToggle 看到的 showDialog 已是 false，X 关闭的 dismiss 埋点永远发不出去
div
  bottom-popup-dialog(:show="showDialog" @changeShow="onDialogToggle")
    template(v-slot:popup-dialog)
      .a2hs_content
        img.a2hs_logo(v-if="venueLogo" :src="venueLogo" alt="")
        .a2hs_title {{ dialogTitle }}
        .a2hs_benefit {{ t('a2hs_benefit', 'Next time, open us straight from your home screen — no QR code needed.') }}
        //- Android 只有拿到 beforeinstallprompt 事件才给一键按钮（手动打开时
        //- 事件可能没到——已装过、条件不满足、隐身模式），否则和 iOS 一样走
        //- 引导路径，绝不摆一个点了没反应的死按钮。事件中途到达按钮实时升级。
        template(v-if="isAndroid && installReady")
          van-button.a2hs_btn.b.ls1(type="primary" block @click="onInstallClick") {{ t('a2hs_add_button', 'ADD TO HOME SCREEN') }}
          .a2hs_later(@click="onLater") {{ t('a2hs_later', 'Maybe later') }}
        template(v-else)
          van-button.a2hs_btn.b.ls1(type="primary" block @click="onShowMe") {{ t('a2hs_show_me', 'SHOW ME HOW') }}
          .a2hs_later(@click="onLater") {{ t('a2hs_later', 'Maybe later') }}
  //- 全屏引导遮罩：业内通行的实底卡片模式（参考 pwa-install / react-ios-pwa-prompt）——
  //- 步骤行放在白色卡片里，文字绝不与页面内容叠字。卡片锚在真实入口那一侧
  //- （iPhone 分享按钮在底部 → 卡片在下；iPad 分享/Android ⋮ 在右上 → 卡片在上），
  //- 弹跳箭头指向入口。v-if 不打开完全不渲染；动画只用 transform/opacity。
  .a2hs_overlay(v-if="showGuideOverlay" @click="closeGuideOverlay")
    .a2hs_guide_card(:class="{ a2hs_guide_card_top: guideEntryAtTop }")
      .a2hs_guide_title {{ dialogTitle }}
      .a2hs_guide_step
        .a2hs_guide_step_icon
          //- Android Chrome：⋮ 竖排三点
          svg(v-if="isAndroid && androidBrand === 'chrome'" viewBox="0 0 24 24" width="22" height="22" fill="currentColor")
            circle(cx="12" cy="5" r="1.9")
            circle(cx="12" cy="12" r="1.9")
            circle(cx="12" cy="19" r="1.9")
          //- Edge：··· 横排三点
          svg(v-else-if="isAndroid && androidBrand === 'edge'" viewBox="0 0 24 24" width="22" height="22" fill="currentColor")
            circle(cx="5" cy="12" r="1.9")
            circle(cx="12" cy="12" r="1.9")
            circle(cx="19" cy="12" r="1.9")
          //- 三星及其余 Chromium：≡ 三横线（三星菜单按钮即此形）
          svg(v-else-if="isAndroid" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round")
            path(d="M4 7h16")
            path(d="M4 12h16")
            path(d="M4 17h16")
          //- iOS：分享字形（方框 + 上箭头）
          svg(v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round")
            path(d="M8.5 6.5l3.5 -3.5l3.5 3.5")
            path(d="M12 3v12")
            path(d="M8 10h-2.5v10.5h13v-10.5h-2.5")
        .a2hs_guide_step_text {{ overlayHint }}
      .a2hs_guide_step
        .a2hs_guide_step_icon
          //- Android：Chrome 菜单里「Add to Home screen」的手机+字形
          svg(v-if="isAndroid" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round")
            rect(x="6.5" y="2.5" width="11" height="19" rx="2")
            path(d="M12 9v6")
            path(d="M9 12h6")
          //- iOS：分享菜单里「Add to Home Screen」的方框加号字形
          svg(v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round")
            rect(x="3.5" y="3.5" width="17" height="17" rx="4")
            path(d="M12 8.5v7")
            path(d="M8.5 12h7")
        .a2hs_guide_step_text {{ overlayStepChoose }}
      .a2hs_guide_close {{ t('a2hs_overlay_close', 'Tap anywhere to close') }}
    //- 箭头指向真实入口：位置不确定的浏览器（opera/other）不显示，免得指错
    svg.a2hs_arrow(v-if="guideArrowPosition" :class="{ a2hs_arrow_ipad: guideArrowPosition === 'top-right', a2hs_arrow_bottom_right: guideArrowPosition === 'bottom-right' }" viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round")
      path(d="M12 3v16")
      path(d="M5.5 12.5l6.5 6.5l6.5 -6.5")
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { t, tParams } from '@/utils/language'
import {
  detectA2hsPlatform,
  detectAndroidBrowserBrand,
  canPromptAddToHomeScreen,
  readA2hsEnvironment,
  isIpadLikeDevice,
  A2HS_PLATFORM,
  ANDROID_BROWSER_BRAND
} from '@/utils/addToHomeScreen/platform'
import {
  hasPromptedForVenue,
  markPromptedForVenue
} from '@/utils/addToHomeScreen/promptRecord'
import {
  initInstallPromptCapture,
  onInstallAvailable,
  onAppInstalled,
  showInstallPrompt,
  clearCapturedInstallPrompt
} from '@/utils/addToHomeScreen/installPrompt'
import { injectVenueManifest, removeVenueManifest } from '@/utils/addToHomeScreen/manifestInjection'
import { A2HS_MANUAL_OPEN_EVENT } from '@/utils/addToHomeScreen/manualEntry'
import { isHomeScreenEntryHere } from '@/utils/addToHomeScreen/attribution'

// Delay before the popup may appear: a timer OR a real scroll, whichever
// happens first. Never interrupt the first seconds of menu browsing.
// 弹窗出现前的等待：计时器或真实滚动，先到先触发。绝不打断刚进菜单的头几秒。
const SHOW_DELAY_MS = 8000
const SCROLL_TRIGGER_PX = 150

@Component({
  props: {
    // Backend per-venue flag: venueInfo.settings.options.enableAddToHomeScreen
    enabled: Boolean,
    venueId: {
      type: String,
      default: ''
    },
    venueName: {
      type: String,
      default: ''
    },
    venueLogo: {
      type: String,
      default: ''
    },
    // 店家自配的桌面短名（venueInfo.homeScreenName），空则回落店名词边界截断
    homeScreenName: {
      type: String,
      default: ''
    },
    // Present on dine-in URLs; the popup is pickup-only. 堂食带桌号，只在 pickup 弹。
    tableNumber: {
      type: [String, Number],
      default: ''
    },
    // Read-only mode (/r/ alias): menu is view-only, a saved entry could never
    // order — never prompt there. 只读模式存下的入口永远无法下单，不弹。
    readOnly: Boolean,
    // SP mode (/v/sp/): separate flow, excluded until product decides.
    spMode: Boolean
  },

  watch: {
    enabled() {
      this.refresh()
    },
    venueId() {
      this.refresh()
    },
    tableNumber() {
      this.refresh()
    },
    readOnly() {
      this.refresh()
    },
    spMode() {
      this.refresh()
    }
  }
})
class AddToHomeScreenPrompt extends Vue {
  showDialog = false
  showGuideOverlay = false
  installReady = false
  armed = false
  hasShown = false
  // 本次弹窗因何打开：'auto' 自动弹 / 'menu' 抽屉手动开。outcome/dismiss 埋点
  // 都带上它，漏斗才能按入口分开算——手动入口可反复打开，混在一起会稀释自动弹的转化率
  openSource = ''

  // non-reactive handles / 非响应式句柄
  setupVenueId = ''
  timerId = null
  unsubscribeAvailable = null
  unsubscribeInstalled = null
  scrollHandler = null
  visibilityHandler = null

  get platform() {
    const { userAgent, maxTouchPoints } = readA2hsEnvironment()
    return detectA2hsPlatform(userAgent, { maxTouchPoints })
  }

  get isAndroid() {
    return this.platform === A2HS_PLATFORM.ANDROID_CHROME
  }

  // Chromium 系里的具体品牌：决定引导措辞与箭头位置，埋点分维度
  get androidBrand() {
    const { userAgent } = readA2hsEnvironment()
    return detectAndroidBrowserBrand(userAgent)
  }

  // 埋点用的浏览器维度：只在 Android 有意义（iOS 只放行 Safari）
  get analyticsBrowser() {
    return this.isAndroid ? this.androidBrand : undefined
  }

  get isIpad() {
    const { userAgent, maxTouchPoints } = readA2hsEnvironment()
    return isIpadLikeDevice(userAgent, { maxTouchPoints })
  }

  // 引导卡片第一步的提示语：按真实入口位置分平台/品牌（Chrome ⋮ 右上 /
  // 三星 ≡ 右下 / Edge ··· 底部 / 其余 Chromium 位置不确定给通用文案 /
  // iPad 分享右上 / iPhone 分享底部）
  get overlayHint() {
    // eslint-disable-next-line no-unused-expressions
    this.$lang
    if (this.isAndroid) {
      switch (this.androidBrand) {
        case ANDROID_BROWSER_BRAND.SAMSUNG:
          return t('a2hs_overlay_menu_samsung', 'Tap the ≡ menu at the bottom right')
        case ANDROID_BROWSER_BRAND.EDGE:
          return t('a2hs_overlay_menu_edge', 'Tap the ··· menu at the bottom')
        case ANDROID_BROWSER_BRAND.CHROME:
          return t('a2hs_overlay_menu_android', 'Tap the ⋮ menu at the top right')
        default:
          return t('a2hs_overlay_menu_generic', 'Open your browser menu')
      }
    }
    if (this.isIpad) return t('a2hs_overlay_share_ipad', 'Tap the Share button at the top right')
    return t('a2hs_overlay_share_iphone', 'Tap the Share button below')
  }

  // 第二步：措辞与各家菜单原文一致（iOS「Add to Home Screen」大写 S，
  // Chrome「Add to Home screen」小写 s，三星是两级菜单，Edge 叫「Add to
  // phone」），否则顾客对不上号。iOS 加「往下翻」——分享菜单里该项常在折叠区
  get overlayStepChoose() {
    // eslint-disable-next-line no-unused-expressions
    this.$lang
    if (this.isAndroid) {
      let item
      switch (this.androidBrand) {
        case ANDROID_BROWSER_BRAND.SAMSUNG:
          item = t('a2hs_samsung_menu_add', 'Add page to › Home screen')
          break
        case ANDROID_BROWSER_BRAND.EDGE:
          item = t('a2hs_edge_menu_add', 'Add to phone')
          break
        default:
          item = t('a2hs_android_menu_add', 'Add to Home screen')
      }
      return tParams('a2hs_overlay_choose', 'Choose “{item}”', { item })
    }
    return tParams('a2hs_overlay_scroll_choose', 'Scroll down, choose “{item}”', {
      item: t('a2hs_ios_step_add_short', 'Add to Home Screen')
    })
  }

  // 箭头指向真实入口的位置；不确定的品牌返回 ''（不显示箭头，免得指错）
  get guideArrowPosition() {
    if (this.isAndroid) {
      switch (this.androidBrand) {
        case ANDROID_BROWSER_BRAND.CHROME: return 'top-right'
        case ANDROID_BROWSER_BRAND.SAMSUNG: return 'bottom-right'
        case ANDROID_BROWSER_BRAND.EDGE: return 'bottom-center'
        default: return ''
      }
    }
    return this.isIpad ? 'top-right' : 'bottom-center'
  }

  // 入口在顶部的形态：卡片锚顶部让出箭头区，其余锚底部
  get guideEntryAtTop() {
    return this.guideArrowPosition === 'top-right'
  }

  get dialogTitle() {
    // eslint-disable-next-line no-unused-expressions
    this.$lang
    return tParams('a2hs_title', 'Add {venue} to your home screen', {
      venue: this.venueName || t('a2hs_this_restaurant', 'this restaurant')
    })
  }

  t(key, fallback) {
    // eslint-disable-next-line no-unused-expressions
    this.$lang
    return t(key, fallback)
  }

  mounted() {
    this.refresh()
    // 抽屉菜单的手动入口通过 $root 事件请求打开（见 utils/addToHomeScreen/manualEntry.js）
    this.$root.$on(A2HS_MANUAL_OPEN_EVENT, this.onManualOpen)
  }

  beforeDestroy() {
    this.$root.$off(A2HS_MANUAL_OPEN_EVENT, this.onManualOpen)
    this.disarm()
  }

  /** All eligibility gates in one place — used by refresh AND re-checked at show time. */
  get isEligible() {
    return !!(
      this.enabled &&
      this.venueId &&
      !this.tableNumber &&
      !this.readOnly &&
      !this.spMode &&
      canPromptAddToHomeScreen(readA2hsEnvironment())
    )
  }

  /**
   * Single entry point for every watched prop change. Venue switch resets all
   * per-venue state first (component instances are reused across SPA
   * navigations); turning ineligible disarms completely.
   * 所有 watch 的唯一入口。换店先重置全部按店状态（SPA 导航会复用组件实例）；
   * 变为不适用则整体解除武装。
   */
  refresh() {
    if (this.setupVenueId && this.setupVenueId !== this.venueId) {
      // 换店：作废 A 店的一切——已捕获的安装事件不带 venue 身份，残留的
      // manifest 会让 Chrome 菜单装出上一家店的 app
      this.disarm()
      this.hasShown = false
      this.armed = false
      this.installReady = false
      this.showDialog = false
      this.showGuideOverlay = false
    }
    if (!this.isEligible) {
      if (this.setupVenueId) this.disarm()
      return
    }
    this.trySetup()
  }

  /**
   * Set up the current venue: install pipeline always, auto-popup triggers
   * only while the one-shot record is unburned. Runs once per venueId.
   * 为当前店做准备：安装管线每店必做，自动弹窗触发器只在一次性记录未烧掉时装。
   * 每个 venueId 只跑一次。
   *
   * The split matters for the drawer's manual entry: its target audience is
   * exactly the already-prompted customers, and without the pipeline the
   * Android button would be permanently dead for them (no manifest, no
   * captured event).
   * 拆开是为了抽屉手动入口：它的目标人群恰恰是弹过的人，不装管线的话 Android
   * 按钮对他们永远是死的（没 manifest、没捕获事件）。
   */
  trySetup() {
    if (this.setupVenueId === this.venueId) return
    this.setupVenueId = this.venueId

    // iOS 书签图标/名字注入不在这里——它挂在 venue-home 的 _postVenue（本组件
    // 只在 pickup 无桌号页挂载，顾客却可能从 dine-in 页、cart 页走分享菜单保存）

    // 捕获监听必须先于 manifest 注入，事件实测注入后 ~300ms 就到
    initInstallPromptCapture()
    if (this.isAndroid) {
      injectVenueManifest(this.venueId)
      this.unsubscribeAvailable = onInstallAvailable(() => {
        this.installReady = true
        this.maybeShow()
      })
      // 用户走 Chrome 菜单自行安装时，弹窗若还开着就是个死按钮——直接收起
      this.unsubscribeInstalled = onAppInstalled(() => {
        this.showDialog = false
      })
    }

    // 从桌面图标进来的（URL 带 src=home）：顾客已经保存过了，自动弹是在推销
    // 人家已有的东西——本次访问不装自动触发器。抽屉手动入口照常可用（删了
    // 图标想重新加的场景）。兜的是「清过存储 + 从图标回访」这类 promptRecord
    // 丢失的情况；不写 promptRecord，与手动打开「不烧一次性机会」的语义一致。
    if (isHomeScreenEntryHere()) return

    // 自动弹窗每店一次；弹过就不再装触发器（手动入口不受此门槛限制）
    if (hasPromptedForVenue(this.venueId)) return

    this.timerId = setTimeout(() => {
      this.armed = true
      this.teardownTriggers() // 计时器先到也要摘掉滚动监听，与滚动分支对称
      this.maybeShow()
    }, SHOW_DELAY_MS)

    this.scrollHandler = () => {
      if (window.scrollY > SCROLL_TRIGGER_PX) {
        this.armed = true
        this.teardownTriggers()
        this.maybeShow()
      }
    }
    window.addEventListener('scroll', this.scrollHandler, { passive: true })

    // 曝光被后台标签页吞掉是白烧机会：计时器到点时若不可见，等回到前台再弹
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') this.maybeShow()
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  /**
   * Fully stand down: triggers, subscriptions, captured event, manifest.
   * 完全解除武装：触发器、订阅、已捕获事件、manifest 一并清掉。
   */
  disarm() {
    this.teardownTriggers()
    if (this.unsubscribeAvailable) {
      this.unsubscribeAvailable()
      this.unsubscribeAvailable = null
    }
    if (this.unsubscribeInstalled) {
      this.unsubscribeInstalled()
      this.unsubscribeInstalled = null
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    clearCapturedInstallPrompt()
    removeVenueManifest()
    // iOS 书签 meta 刻意不在这里撤：注入归 venue-home 管，组件卸载（比如
    // 逛去 cart 页）不该把品牌图标撤掉——顾客在那里保存也要拿到店铺图标
    this.setupVenueId = ''
  }

  maybeShow() {
    if (this.hasShown || !this.armed) return
    // 弹前复查全部门槛：武装期间路由/开关可能已经变了
    if (!this.isEligible || this.setupVenueId !== this.venueId) return
    // 后台标签页里弹了也没人看见，机会却烧掉了——等 visibilitychange 再来
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    // Android waits for the captured event — without it the button would be
    // dead (already installed, or criteria unmet). iOS shows unconditionally.
    // Android 必须等到事件才弹，否则按钮是死的（已安装或条件未满足）；iOS 直接弹。
    if (this.isAndroid && !this.installReady) return
    if (hasPromptedForVenue(this.venueId)) return

    // iOS 保存的是当前完整 URL——先清掉易变参数，免得 ?qr=1 冻进书签污染扫码
    // 统计、?tableNumber 把顾客绑到实体桌
    this.stripVolatileQueryParams()

    this.hasShown = true
    this.openSource = 'auto'
    this.showDialog = true
    markPromptedForVenue(this.venueId)
    this.$emit('a2hsExposure', { platform: this.platform, browser: this.analyticsBrowser, venueId: this.venueId, source: 'auto' })
  }

  /**
   * Manual open from the drawer menu ($root event). Skips the armed /
   * hasPrompted gates on purpose and does NOT markPromptedForVenue — the
   * customer asked for the popup, that must not burn the one-shot auto prompt.
   * On Android, if the captured install event hasn't arrived yet the button
   * yields outcome=unavailable — accepted trade-off.
   * 抽屉菜单的手动打开（$root 事件）。刻意不走 armed/hasPrompted 门槛，也不
   * markPromptedForVenue——顾客主动点的不烧一次性机会。Android 侧若安装事件
   * 尚未捕获，按钮会得到 outcome=unavailable，可接受。
   */
  onManualOpen() {
    if (!this.isEligible) return
    if (this.showDialog) return
    // 幂等兜底：正常时序下 refresh() 早已为本店跑过安装管线（含弹过的店），
    // 这里只防御事件先于 refresh 到达的极端时序
    this.trySetup()
    this.stripVolatileQueryParams()
    // 手动看过了，本次挂载期间不再自动弹（离开页面再回来组件重建，自动弹恢复
    // 基线行为）；不写 promptRecord，下次进店自动弹照旧
    this.hasShown = true
    this.teardownTriggers() // 自动弹的计时器/滚动监听已无意义，直接摘掉
    this.openSource = 'menu'
    this.showGuideOverlay = false
    this.showDialog = true
    this.$emit('a2hsExposure', { platform: this.platform, browser: this.analyticsBrowser, venueId: this.venueId, source: 'menu' })
  }

  /**
   * Clean the address bar right before the customer may save it: drop
   * volatile params (qr / tableNumber) and stamp `src=home` so a visit
   * through the saved iOS entry is attributable. Android gets the same
   * marker from the manifest start_url.
   *
   * `portal` is deliberately KEPT: it is a functional param, not analytics —
   * cart hides the order-type picker on it and cart/contact-details/pay use it
   * for takeaway/counter mode detection (the router guard even re-propagates
   * it across navigations). Reopening from the icon is semantically a fresh
   * walk through the same portal door, which would hand out portal=true again.
   *
   * 在顾客可能保存前清理地址栏：去掉易变参数（qr / tableNumber）并盖上
   * `src=home` 归因标记（Android 侧由 manifest 的 start_url 携带同一标记）。
   * `portal` 刻意保留：它是功能参数不是埋点——cart 靠它隐藏订单类型选择，
   * cart/contact-details/pay 靠它判定 takeaway/counter 环境，router 守卫还会
   * 跨页透传。从图标回来等价于重走一遍同一个短链入口，重扫也会再拿到它。
   */
  stripVolatileQueryParams() {
    try {
      const url = new URL(window.location.href)
      // previewMenu（菜单预览码，多版本菜单 B6 U-B6-3）：预览页本来就不弹（canPromptAddToHomeScreen 的 isPreviewPage），
      // 这里再剥一次是纵深 —— 码冻进书签，从图标进来就永远下不了单（后端对带码的单一律拒）
      const volatile = ['qr', 'tableNumber', 'previewMenu']
      let changed = false
      volatile.forEach(key => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key)
          changed = true
        }
      })
      if (url.searchParams.get('src') !== 'home') {
        url.searchParams.set('src', 'home')
        changed = true
      }
      if (changed) {
        window.history.replaceState(window.history.state, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash)
      }
    } catch (e) {
      // URL/history 不可用就算了，弹窗照常
    }
  }

  async onInstallClick() {
    const { outcome } = await showInstallPrompt()
    this.$emit('a2hsOutcome', { outcome, venueId: this.venueId, source: this.openSource, platform: this.platform, browser: this.analyticsBrowser })
    this.showDialog = false
    // 事件过期/被消费的竞态兜底：按钮不能无声失败，退回引导教顾客走 Chrome 菜单
    if (outcome === 'unavailable') this.showGuideOverlay = true
  }

  onLater() {
    this.$emit('a2hsDismiss', { platform: this.platform, browser: this.analyticsBrowser, venueId: this.venueId, source: this.openSource })
    this.showDialog = false
  }

  // 收起弹窗、展开指向真实入口的全屏引导（iOS 分享按钮 / Android 无安装事件时
  // 的 ⋮ 菜单）。计一次 outcome=guide 供漏斗对比，platform 字段区分两条路径
  onShowMe() {
    this.$emit('a2hsOutcome', { outcome: 'guide', venueId: this.venueId, source: this.openSource, platform: this.platform, browser: this.analyticsBrowser })
    this.showDialog = false
    this.showGuideOverlay = true
  }

  closeGuideOverlay() {
    this.showGuideOverlay = false
  }

  onDialogToggle(show) {
    if (!show && this.showDialog) {
      this.$emit('a2hsDismiss', { platform: this.platform, browser: this.analyticsBrowser, venueId: this.venueId, source: this.openSource })
    }
    this.showDialog = show
  }

  teardownTriggers() {
    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }
  }
}

export default AddToHomeScreenPrompt
</script>
